import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { WondeSyncMismatch } from '@core/models/domain/wonde-sync.domain';
import {
  AssignGroupsForWondeSyncMismatchRequest,
  CreatePupilForWondeSyncMismatchRequest,
  DismissWondeSyncMismatchRequest,
  LinkWondeSyncMismatchRequest
} from '@core/models/request/wonde-sync.request';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * WondeSyncService
 *
 * Backend calls for the school admin "Review mismatches" page: pupil records the last MIS sync
 * could not match to exactly one school pupil, and the four decisions an admin can take (link
 * keeping the school's data, link and take the MIS data, create a new pupil, or skip), plus assigning
 * the groups of a pupil whose year the sync could not work out. The school
 * is taken from the caller's token by the backend, never sent from here.
 */
@Injectable({
  providedIn: 'root'
})
export class WondeSyncService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL("UserService", "school-account");
  }

  loadMismatches(): Observable<WondeSyncMismatch[]> {
    return this.http
      .get<DigiResponse<WondeSyncMismatch[]>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches"))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Loading MIS records failed");
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<WondeSyncMismatch[]>("Loading MIS records", error))
      );
  }

  /** Records the school skipped: they can still be added as a new pupil or linked (DIGI-1864). */
  loadSkippedMismatches(): Observable<WondeSyncMismatch[]> {
    return this.http
      .get<DigiResponse<WondeSyncMismatch[]>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches/skipped"))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Loading skipped MIS records failed");
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<WondeSyncMismatch[]>("Loading skipped MIS records", error))
      );
  }

  link(request: LinkWondeSyncMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches/link"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Linking the pupil failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Linking pupil", error))
      );
  }

  createPupil(request: CreatePupilForWondeSyncMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches/create-pupil"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Creating the pupil failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Creating pupil", error))
      );
  }

  dismiss(request: DismissWondeSyncMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches/dismiss"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Skipping the MIS record failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Skipping MIS record", error))
      );
  }

  assignGroups(request: AssignGroupsForWondeSyncMismatchRequest): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "wonde-sync-mismatches/assign-groups"), request)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Assigning the groups failed");
          }
          return request.mismatchId;
        }),
        catchError((error) => handleError<string>("Assigning groups", error))
      );
  }
}
